import { IRuntimeMetadataFacetDiff, IRuntimeMetadataFacetGroup, MetadataFacetGroupKey } from '@edgebox/sync-core-data-definitions';
import DOMPurify from 'dompurify';
import { Button } from '@edgebox/react-components';
import React from 'react';
import { BP_CARD_CLASSES } from '../../Governance/Components/brandPresenceClasses';

const FACET_GROUP_ORDER: MetadataFacetGroupKey[] = [
  MetadataFacetGroupKey.Metatags,
  MetadataFacetGroupKey.OpenGraph,
  MetadataFacetGroupKey.Twitter,
  MetadataFacetGroupKey.SchemaOrg,
  MetadataFacetGroupKey.Other,
];

const FACET_GROUP_LABELS: Record<MetadataFacetGroupKey, string> = {
  [MetadataFacetGroupKey.Metatags]: 'Meta tags',
  [MetadataFacetGroupKey.OpenGraph]: 'OpenGraph',
  [MetadataFacetGroupKey.Twitter]: 'Twitter',
  [MetadataFacetGroupKey.SchemaOrg]: 'Schema.org',
  [MetadataFacetGroupKey.Other]: 'Other',
};

const COLLAPSED_BY_DEFAULT: Set<MetadataFacetGroupKey> = new Set([
  MetadataFacetGroupKey.Twitter,
  MetadataFacetGroupKey.SchemaOrg,
  MetadataFacetGroupKey.Other,
]);

type Decision = 'pending' | 'approved' | 'discarded';

type ChangeKind = 'added' | 'removed' | 'modified';

const CHANGE_KIND_LABELS: Record<ChangeKind, string> = {
  added: 'Added',
  removed: 'Removed',
  modified: 'Modified',
};

const CHANGE_KIND_BADGE_VARIANT: Record<ChangeKind, string> = {
  added: 'bg-success',
  removed: 'bg-danger',
  modified: 'bg-warning',
};

export interface IMetadataReviewDecisionSnapshot {
  approvedSourceChangeIds: string[];
  totalApprovableCount: number;
}

interface IProps {
  metadataChanges: IRuntimeMetadataFacetDiff[];
  metadataChangeGroups?: IRuntimeMetadataFacetGroup[];
  onDecisionsChange?: (snapshot: IMetadataReviewDecisionSnapshot) => void;
}

function getChangeKind(facet: IRuntimeMetadataFacetDiff): ChangeKind {
  const hasSource = typeof facet.sourceValue === 'string' && facet.sourceValue.length > 0;
  const hasTarget = typeof facet.targetValue === 'string' && facet.targetValue.length > 0;

  if (!hasSource && hasTarget) {
    return 'added';
  }

  if (hasSource && !hasTarget) {
    return 'removed';
  }

  return 'modified';
}

function isApprovable(facet: IRuntimeMetadataFacetDiff): boolean {
  return typeof facet.entityChangeId === 'string' && facet.entityChangeId.length > 0;
}

function groupFacets(
  metadataChanges: IRuntimeMetadataFacetDiff[]
): Array<{ group: MetadataFacetGroupKey; facets: IRuntimeMetadataFacetDiff[] }> {
  const byGroup = new Map<MetadataFacetGroupKey, IRuntimeMetadataFacetDiff[]>();

  metadataChanges.forEach((facet) => {
    const groupKey = (facet.group as MetadataFacetGroupKey) || MetadataFacetGroupKey.Other;
    if (!byGroup.has(groupKey)) {
      byGroup.set(groupKey, []);
    }
    byGroup.get(groupKey)!.push(facet);
  });

  return FACET_GROUP_ORDER.filter((group) => byGroup.has(group)).map((group) => ({
    group,
    facets: byGroup.get(group)!,
  }));
}

export const MetadataReview: React.FC<IProps> = ({ metadataChanges, metadataChangeGroups, onDecisionsChange }) => {
  const [decisions, setDecisions] = React.useState<Record<string, Decision>>({});
  const [collapsed, setCollapsed] = React.useState<Record<string, boolean>>(() => {
    const initial: Record<string, boolean> = {};
    FACET_GROUP_ORDER.forEach((group) => {
      initial[group] = COLLAPSED_BY_DEFAULT.has(group);
    });
    return initial;
  });

  const sortedGroups = React.useMemo(() => groupFacets(metadataChanges), [metadataChanges]);

  React.useEffect(() => {
    setDecisions((previous) => {
      const next: Record<string, Decision> = {};
      metadataChanges.forEach((facet) => {
        const sourceChangeId = facet.sourceChangeId;
        if (!sourceChangeId) {
          return;
        }

        next[sourceChangeId] = previous[sourceChangeId] || 'pending';
      });
      return next;
    });
  }, [metadataChanges]);

  React.useEffect(() => {
    if (!onDecisionsChange) {
      return;
    }

    const approvableSourceChangeIds = metadataChanges.filter(isApprovable).map((facet) => facet.sourceChangeId);
    const approvedSourceChangeIds = approvableSourceChangeIds.filter((sourceChangeId) => decisions[sourceChangeId] === 'approved');

    onDecisionsChange({
      approvedSourceChangeIds,
      totalApprovableCount: approvableSourceChangeIds.length,
    });
  }, [decisions, metadataChanges, onDecisionsChange]);

  if (!metadataChanges.length) {
    return null;
  }

  const setDecision = (sourceChangeId: string, decision: Decision) => {
    setDecisions((previous) => ({ ...previous, [sourceChangeId]: decision }));
  };

  const setGroupDecision = (groupKey: MetadataFacetGroupKey, decision: Decision) => {
    setDecisions((previous) => {
      const next = { ...previous };
      sortedGroups
        .find((entry) => entry.group === groupKey)
        ?.facets.filter(isApprovable)
        .forEach((facet) => {
          if (facet.sourceChangeId) {
            next[facet.sourceChangeId] = decision;
          }
        });
      return next;
    });
  };

  const toggleGroup = (groupKey: MetadataFacetGroupKey) => {
    setCollapsed((previous) => ({ ...previous, [groupKey]: !previous[groupKey] }));
  };

  const groupHasReviewableFacets = (groupKey: MetadataFacetGroupKey): boolean => {
    const entry = sortedGroups.find((candidate) => candidate.group === groupKey);
    return Boolean(entry?.facets.some(isApprovable));
  };

  const renderDecisionBadge = (decision: Decision) => {
    if (decision === 'approved') {
      return <span className="badge bg-success">Approved</span>;
    }

    if (decision === 'discarded') {
      return <span className="badge bg-secondary">Discarded</span>;
    }

    return null;
  };

  return (
    // The section is one of the App's cards, drawn from its recipes: the Optimize tab serves it inside the
    // design system's scope, where a bare Bootstrap class of the same name as a utility (border, rounded,
    // w-100) would take the utility's meaning instead of Bootstrap's.
    <section className={`metadata-review overflow-hidden ${BP_CARD_CLASSES}`} aria-label="Metadata review">
      <header className="flex items-center justify-between gap-3 border-b border-slate-200/70 px-6 py-4">
        <div>
          <h5 className="m-0 text-base font-semibold text-slate-900">Metadata changes</h5>
          <small className="mt-0.5 block text-sm text-slate-500">
            Review changes that aren&apos;t visible in the rendered content above the article body.
          </small>
        </div>
      </header>

      {sortedGroups.map(({ group, facets }) => {
        const groupId = `metadata-group-${group}`;
        const groupLabel = FACET_GROUP_LABELS[group];
        const isCollapsed = collapsed[group] === true;
        const groupDescriptor = metadataChangeGroups?.find((candidate) => (candidate.group as MetadataFacetGroupKey) === group);
        const canBulkAct = groupHasReviewableFacets(group);

        return (
          <div key={group} className="metadata-review-group border-b border-slate-100 last:border-b-0" data-testid={groupId}>
            <button
              type="button"
              className="flex w-full items-center justify-between gap-3 border-0 bg-transparent px-6 py-3 text-left text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
              aria-expanded={!isCollapsed}
              aria-controls={`${groupId}-rows`}
              onClick={() => toggleGroup(group)}
            >
              <span className="font-semibold text-slate-900">
                {groupDescriptor?.label || groupLabel}
                <span className="ml-2 font-normal text-slate-500">({facets.length})</span>
              </span>
              <span className="text-xs font-semibold text-slate-600">{isCollapsed ? 'Show' : 'Hide'}</span>
            </button>

            {!isCollapsed ? (
              <div id={`${groupId}-rows`} className="px-6 pb-4">
                {canBulkAct ? (
                  <div className="mb-2 flex gap-2">
                    <Button variant="light" size="sm" onClick={() => setGroupDecision(group, 'approved')}>
                      Approve all
                    </Button>
                    <Button variant="light" size="sm" onClick={() => setGroupDecision(group, 'discarded')}>
                      Discard all
                    </Button>
                    <Button variant="link" size="sm" onClick={() => setGroupDecision(group, 'pending')}>
                      Reset
                    </Button>
                  </div>
                ) : null}

                <ul className="list-unstyled mb-0">
                  {facets.map((facet) => {
                    const sourceChangeId = facet.sourceChangeId;
                    const decision = (sourceChangeId && decisions[sourceChangeId]) || 'pending';
                    const kind = getChangeKind(facet);
                    const sanitizedHtml = DOMPurify.sanitize(facet.inlineDiffHtml || '');
                    const approvable = isApprovable(facet);

                    return (
                      <li
                        key={facet.key}
                        className="metadata-review-row flex flex-col gap-2 border-t border-slate-100 py-3 md:flex-row md:items-start"
                        data-testid={`metadata-row-${facet.key}`}
                      >
                        <div className="metadata-review-row-label shrink-0" style={{ minWidth: '160px' }}>
                          <div className="flex flex-wrap items-center gap-2">
                            <span className={`badge ${CHANGE_KIND_BADGE_VARIANT[kind]}`}>{CHANGE_KIND_LABELS[kind]}</span>
                            <span className="text-sm font-semibold text-slate-900">{facet.label}</span>
                          </div>
                          <div className="text-xs text-slate-500">
                            <code>{facet.key}</code>
                          </div>
                          {!approvable ? (
                            <div className="mt-1 text-xs text-slate-500">Review only — not yet applicable on the host CMS.</div>
                          ) : null}
                        </div>

                        <div className="metadata-review-row-diff min-w-0 flex-1 [overflow-wrap:anywhere]">
                          <div
                            className="cs-diff-host"
                            // The backend pre-computes inlineDiffHtml using the same word-diff algorithm
                            // as the body diff, so the cs-diff-word-add / cs-diff-word-remove classes
                            // align styling between the metadata box and the body diff.
                            dangerouslySetInnerHTML={{ __html: sanitizedHtml }}
                          />
                        </div>

                        <div className="metadata-review-row-actions flex shrink-0 items-center gap-2">
                          {renderDecisionBadge(decision)}
                          {approvable && sourceChangeId ? (
                            <div className="btn-group" role="group" aria-label={`Decision for ${facet.label}`}>
                              <button
                                type="button"
                                className={`btn btn-sm ${decision === 'approved' ? 'btn-success' : 'btn-outline-success'}`}
                                onClick={() => setDecision(sourceChangeId, decision === 'approved' ? 'pending' : 'approved')}
                              >
                                Approve
                              </button>
                              <button
                                type="button"
                                className={`btn btn-sm ${decision === 'discarded' ? 'btn-secondary' : 'btn-outline-secondary'}`}
                                onClick={() => setDecision(sourceChangeId, decision === 'discarded' ? 'pending' : 'discarded')}
                              >
                                Discard
                              </button>
                            </div>
                          ) : null}
                        </div>
                      </li>
                    );
                  })}
                </ul>
              </div>
            ) : null}
          </div>
        );
      })}
    </section>
  );
};
